'use client';

import { useEffect, useState } from 'react';

/**
 * The living numbers on the games home dialog (backend routes/games.ts):
 *
 *   • clicks  — how many times the game has been opened off the shelf, guests
 *               included. Top-of-funnel interest.
 *   • plays   — total counted plays (a game left open ≥10s), signed-in.
 *   • players — how many accounts have played it.
 *   • live    — how many are in it right now (played in the last few minutes).
 *
 * Plus house-wide `totals` (the same four, de-duplicated across games, and the
 * KHARMA that playing has paid out). Polled every POLL_MS while any dialog is
 * open and shared by every reader, exactly like the vote totals next to it.
 *
 * A click is recorded with recordClick(id) — fire-and-forget, best-effort; a
 * dropped beacon never blocks the launch it precedes.
 */

export interface GamePulse { clicks: number; plays: number; players: number; live: number }
export interface PulseTotals extends GamePulse { marqPaid: number }
export type GamePulses = Record<string, GamePulse>;

const EMPTY_TOTALS: PulseTotals = { clicks: 0, plays: 0, players: 0, live: 0, marqPaid: 0 };
const POLL_MS = 5000;
const API = process.env.NEXT_PUBLIC_API_BASE ?? '';
const GAME_ID = /^[a-z0-9]{1,24}$/;

let games: GamePulses = {};
let totals: PulseTotals = EMPTY_TOTALS;
let timer: ReturnType<typeof setInterval> | null = null;
const listeners = new Set<() => void>();

function emit() { listeners.forEach((fn) => fn()); }

async function load() {
  if (typeof document !== 'undefined' && document.hidden) return;
  try {
    const r = await fetch(`${API}/api/games/pulse`, { cache: 'no-store' });
    if (!r.ok) return;
    const j = await r.json();
    if (j && typeof j.games === 'object') games = j.games;
    if (j && typeof j.totals === 'object') totals = { ...EMPTY_TOTALS, ...j.totals };
    emit();
  } catch { /* offline: keep the last numbers */ }
}

/** Count one open of a game. Best-effort; errors are swallowed on purpose. */
export function recordClick(id: string): void {
  if (!GAME_ID.test(id)) return;
  try {
    fetch(`${API}/api/games/click`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ game: id }),
      keepalive: true,
    }).catch(() => {});
    // Nudge this reader's own count up at once, before the next poll lands.
    const g = games[id];
    games = { ...games, [id]: { ...(g ?? { clicks: 0, plays: 0, players: 0, live: 0 }), clicks: (g?.clicks ?? 0) + 1 } };
    totals = { ...totals, clicks: totals.clicks + 1 };
    emit();
  } catch { /* fetch unavailable (SSR): nothing to do */ }
}

/** The shelf-wide pulse, re-read every few seconds while a dialog is open. */
export function useGamePulse(): { games: GamePulses; totals: PulseTotals } {
  const [, setTick] = useState(0);
  useEffect(() => {
    const fn = () => setTick((t) => t + 1);
    listeners.add(fn);
    load();
    if (!timer) timer = setInterval(load, POLL_MS);
    const onVis = () => { if (!document.hidden) load(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      listeners.delete(fn);
      document.removeEventListener('visibilitychange', onVis);
      if (!listeners.size && timer) { clearInterval(timer); timer = null; }
    };
  }, []);
  return { games, totals };
}
